import React, { useEffect, useRef, useState } from "react";
import ArrowDown from "@/assets/icons/ArrowDown.svg";
import BlueCheck from "@/assets/icons/BlueCheck.svg";

type SelectOption = {
  id: string;
  title: string;
};

type MultiSelectProps = {
  label: string;
  options: SelectOption[];
  selectedOptions: SelectOption[];
  onSelect: (options: SelectOption[]) => void;
  placeholder?: string;
  showOtherInputField?: boolean;
};

function MultiSelect({
  label,
  options,
  selectedOptions,
  onSelect,
  placeholder = "Select options...",
  showOtherInputField = true,
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isShowOtherInputField, setShowOtherInputField] = useState(false);
  const [otherInputValue, setOtherInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleAddOtherOption = () => {
    if (otherInputValue.trim() !== "") {
      const newOption: SelectOption = {
        id: new Date().toISOString(),
        title: otherInputValue,
      };
      onSelect([...selectedOptions, newOption]);
      setOtherInputValue("");
      setShowOtherInputField(false);
    }
  };

  const handleShowOtherField = () => {
    setShowOtherInputField(true);
    inputRef?.current?.focus();
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getDisplayValue = () => {
    return selectedOptions.length > 0
      ? selectedOptions.map((option) => option.title).join(", ")
      : placeholder;
  };

  return (
    <div ref={dropdownRef} className="w-full">
      <p className="text-gray-700 font-medium text-caption-large">{label}</p>
      <div className="relative">
        <div
          className={`${
            isOpen ? "bg-[#17457D]/10 rounded-[8px]" : ""
          } p-1 mt-2`}
        >
          <div
            className={`bg-white px-[14px] transition-all duration-300 ease-in-out py-[10px] border flex justify-between ${
              isOpen ? "border-[#4066A7]" : "border-[#D0D5DD]"
            } rounded-[8px] cursor-pointer`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <div className="flex gap-x-2 items-center">
              <p className="text-[#667085] text-[15px] select-none">
                <span className="text-[#667085] font-medium">
                  {getDisplayValue()}
                </span>
              </p>
            </div>
            <div
              className={`transition-all duration-300 ease-in-out ${
                isOpen ? "rotate-180" : "rotate-0"
              }`}
            >
              <ArrowDown />
            </div>
          </div>
        </div>

        {isOpen && (
          <div className="absolute max-h-48 drop-shadow-lg overflow-y-auto px-[16px] py-[19px] mt-2 w-full border border-[#F2F4F7] rounded-[8px] bg-white z-10">
            <div className="flex flex-col">
              {options.map((option) => (
                <SelectOptionItem
                  key={option.id}
                  item={option}
                  isSelected={selectedOptions.some(
                    (item) => item.id === option.id
                  )}
                  onSelect={onSelect}
                  selectedOptions={selectedOptions}
                />
              ))}

              {!isShowOtherInputField && showOtherInputField ? (
                <p
                  onClick={handleShowOtherField}
                  className="text-[15px] cursor-pointer font-medium py-[10px] text-[#101828]"
                >
                  Others
                </p>
              ) : (
                showOtherInputField && (
                  <div className="relative mt-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={otherInputValue}
                      onChange={(e) => setOtherInputValue(e.target.value)}
                      placeholder="Others..."
                      className="p-2 w-full text-caption-large text-gray-500 rounded-[8px] mb-2 border outline-none border-[#4066A7]"
                    />
                    {otherInputValue && (
                      <button
                        onClick={handleAddOtherOption}
                        className="absolute text-caption2-regular bg-[#17457D] text-white px-3 py-1 font-medium rounded-[6px] top-2 right-2"
                      >
                        Add
                      </button>
                    )}
                  </div>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

type SelectOptionItemProps = {
  item: SelectOption;
  isSelected: boolean;
  onSelect: (options: SelectOption[]) => void;
  selectedOptions: SelectOption[];
};

function SelectOptionItem({
  item,
  isSelected,
  onSelect,
  selectedOptions,
}: SelectOptionItemProps) {
  const handleChange = () => {
    if (isSelected) {
      onSelect(selectedOptions.filter((option) => option.id !== item.id));
    } else {
      onSelect([...selectedOptions, item]);
    }
  };

  return (
    <div
      onClick={handleChange}
      className="py-[10px] px-4 rounded-[6px] hover:bg-[#F9FAFB] cursor-pointer flex justify-between items-center"
    >
      <div className="inline-flex gap-x-2">
        <span className="text-[15px] font-medium text-[#101828]">
          {item.title}
        </span>
      </div>
      {isSelected && <BlueCheck />}
    </div>
  );
}

export default MultiSelect;
